/**
 * Couleurs propres à la fiche cours v2, calculées depuis les jetons du site (jamais les couleurs du
 * prototype). Fonctions pures.
 */
import { AA, contrastRatio, ensureContrast, mix, textOnColor, WHITE } from '../../../lib/color';
import type { V2Tokens } from '../../../theme/tokens';

/**
 * Part de la couleur de marque dans le fond du spot de l'invité (le reste : le papier). Demande de Daphné du 08/10
 * (« une couleur plus douce que le noir, peut-être un rouge plus clair ») : 45 %, un rouge clair sur Dynamo (#F19599),
 * nettement plus clair que le spot du client (2,2:1) et plus foncé que les spots libres (2,2:1) et pris (1,8:1).
 */
export const GUEST_TINT = 0.45;

/** Contour du spot de l'invité : 3:1 au moins sur le papier (WCAG 1.4.11, contour d'un composant). */
const RING_RATIO = 3;

export interface ClassColors {
  /**
   * Fond du spot de l'invité : teinte claire de la couleur de marque (GUEST_TINT). Le spot du client est à la couleur
   * de marque pleine, numéro blanc ; celui de l'invité, en teinte claire, numéro à l'encre et contour à la couleur de
   * marque : la différence se lit aussi sans les couleurs (clair / foncé, contour, numéro sombre / blanc).
   */
  guest: string;
  /** Numéro posé sur le fond de l'invité : encre (4,5:1 au moins, le fond est éclairci au besoin). */
  guestOn: string;
  /** Contour du spot et des pastilles de l'invité : la couleur de marque, foncée au besoin pour 3:1 sur le papier. */
  guestRing: string;
  /** Kicker « MARQUE · STUDIO » sur le bandeau encre : couleur d'action éclaircie jusqu'à 4,5:1. */
  heroAccent: string;
  /** Spot « à moi » (vert de la maquette, assombri pour un numéro blanc lisible). */
  mine: string;
  mineOn: string;
  /** Texte vert posé sur le fond de page (kicker « C'est confirmé ») : vert d'état assombri jusqu'à 4,5:1. */
  greenOnCanvas: string;
}

export function classColors(
  tokens: Pick<V2Tokens, 'brand' | 'action' | 'ink' | 'greenText' | 'canvas' | 'paper'>,
): ClassColors {
  const tint = mix(tokens.brand, tokens.paper, GUEST_TINT);
  // Encre sur la teinte ; si elle n'y atteint pas 4,5:1 (marque très sombre sur un papier teinté), la teinte s'éclaircit.
  const guest = contrastRatio(tokens.ink, tint) >= AA ? tint : ensureContrast(tint, tokens.ink, AA);
  const mine = textOnColor(tokens.greenText, tokens.ink, WHITE);
  return {
    guest,
    guestOn: tokens.ink,
    guestRing: ensureContrast(tokens.brand, tokens.paper, RING_RATIO),
    heroAccent: ensureContrast(tokens.action, tokens.ink, AA),
    mine: mine.surface,
    mineOn: mine.text,
    greenOnCanvas: ensureContrast(tokens.greenText, tokens.canvas, AA),
  };
}

/** Variables CSS posées sur la racine de la fiche (préfixe --mmv2-class-*). */
export function classColorVars(colors: ClassColors): Record<string, string> {
  return {
    '--mmv2-class-guest': colors.guest,
    '--mmv2-class-guest-on': colors.guestOn,
    '--mmv2-class-guest-ring': colors.guestRing,
    '--mmv2-class-hero-accent': colors.heroAccent,
    '--mmv2-class-mine': colors.mine,
    '--mmv2-class-mine-on': colors.mineOn,
    '--mmv2-class-green-on-canvas': colors.greenOnCanvas,
  };
}
